import { useGridApiRef } from '@mui/x-data-grid-premium';
import { RiAddLine } from '@remixicon/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { AgButton } from 'src/components/ui';
import { useAvailableHeight } from 'src/hooks/useAvailableHeight';
import useGridExport from 'src/hooks/useGridExport';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import { notify } from 'src/lib/notify';
import { DataGrid, GridToolbarV2, useGridRows } from 'src/modules/grids';

import { useExpenseTypeActivity } from './hooks/useExpenseTypeActivity';
import { useExpenseTypeColumns } from './hooks/useExpenseTypeColumns';
import {
  EXPENSE_TYPES_GRID_STORAGE_KEY,
  type ExpenseTypeRow,
  useExpenseTypeLedgerAccounts,
  useExpenseTypeNameTaken,
  useExpenseTypes,
} from './hooks/useExpenseTypes';
import { useCreateExpenseType, useDeleteExpenseType, useUpdateExpenseType } from './hooks/useExpenseTypeWrites';
import DeleteExpenseTypeModal from './modals/DeleteExpenseTypeModal';
import ExpenseTypeActionModal, { ExpenseAction } from './modals/ExpenseTypeActionModal';

const NAME_TAKEN = 'A charge with this name already exists.';

/**
 * Charge Types, one server page at a time (TAS-3660 P7 BZ74). Rows live in the Apollo cache: an update
 * re-renders its row through the normalized `ExpenseType`, a create or delete re-reads the page.
 */
export default function ExpenseTypesGrid() {
  const apiRef = useGridApiRef();
  const containerRef = useRef<HTMLDivElement>(null);
  const availableHeight = useAvailableHeight(containerRef);
  const [expenseAction, setExpenseAction] = useState<{ type: ExpenseAction; value?: ExpenseTypeRow } | null>(null);
  const [expenseTypeForDeletion, setExpenseTypeForDeletion] = useState<ExpenseTypeRow | null>(null);

  const enhancedGridState = useGridRows({ mode: 'paged', storageKey: EXPENSE_TYPES_GRID_STORAGE_KEY });
  const { rows, loading, pageInfo, totalCount, fetchAllForExport, refetch, reader } =
    useExpenseTypes(enhancedGridState);
  // Opening the tab stays one read: the modal's account choices load when it first opens.
  const { incomeAccounts, payableAccounts } = useExpenseTypeLedgerAccounts({ skip: expenseAction === null });
  const isNameTaken = useExpenseTypeNameTaken();
  const { readActivity } = useExpenseTypeActivity();

  const refetchExpenseType = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      void readActivity(id).catch(() => undefined);
    },
    [readActivity]
  );

  const { create: createExpense } = useCreateExpenseType();

  const { update: updateExpense } = useUpdateExpenseType({
    onError: (error) => {
      notifyUnlessSurfaced(error, 'Could not update the expense type.');
      refetchExpenseType(expenseAction?.value?.id);
    },
  });

  const { remove: deleteExpense } = useDeleteExpenseType({
    onCompleted: () => {
      notify.success('Expense type deleted');
      refetch();
    },
    onError: (error) => {
      notifyUnlessSurfaced(error, 'Could not delete the expense type.');
      refetchExpenseType(expenseTypeForDeletion?.id);
    },
  });

  const handleExpenseAction = useCallback(
    async (input: { name: string } & Record<string, unknown>) => {
      const editingId = expenseAction?.type === ExpenseAction.UPDATING ? expenseAction.value?.id : undefined;
      if (await isNameTaken(input.name, editingId)) {
        notify.error(NAME_TAKEN);
        return;
      }

      if (expenseAction?.type === ExpenseAction.CREATING) {
        const { data } = await createExpense(input as Parameters<typeof createExpense>[0]);
        if (!data) return;

        notify.success('Expense type created');
        refetch();
        return;
      }

      if (editingId === undefined) return;
      const { data } = await updateExpense({ id: editingId, input });
      if (!data) return;

      notify.success('Expense type updated');
    },
    [expenseAction, isNameTaken, createExpense, updateExpense, refetch]
  );

  const handleToggleEnabled = useCallback(
    (row: ExpenseTypeRow) => updateExpense({ id: row.id, input: { disabled: !row.disabled } }),
    [updateExpense]
  );

  const handleEdit = useCallback((row: ExpenseTypeRow) => {
    setExpenseAction({ type: ExpenseAction.UPDATING, value: row });
  }, []);

  const handleDelete = useCallback((row: ExpenseTypeRow) => {
    setExpenseTypeForDeletion(row);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!expenseTypeForDeletion) return Promise.resolve();
    return deleteExpense(expenseTypeForDeletion.id);
  }, [deleteExpense, expenseTypeForDeletion]);

  const handleOpenModal = useCallback(() => {
    setExpenseAction({ type: ExpenseAction.CREATING });
  }, []);

  const columns = useExpenseTypeColumns({
    onEdit: handleEdit,
    onToggleEnabled: handleToggleEnabled,
    onDelete: handleDelete,
  });

  useGridExport(apiRef, 'expense-types', undefined, undefined, fetchAllForExport);

  const toolbar = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        entityName="Charge Type"
        toolbarActionsRight={
          <AgButton variant="primary" onPress={handleOpenModal}>
            New
            <RiAddLine size={16} />
          </AgButton>
        }
      />
    ),
    [apiRef, enhancedGridState, handleOpenModal]
  );

  return (
    <>
      {reader}
      <div ref={containerRef}>
        <DataGrid
          mode="paged"
          rows={rows}
          totalCount={totalCount}
          pageInfo={pageInfo}
          loading={loading}
          enhancedGridState={enhancedGridState}
          columns={columns}
          gridKey="settings.business.expense-types"
          storageKey={EXPENSE_TYPES_GRID_STORAGE_KEY}
          apiRef={apiRef}
          getRowClassName={({ row }) => (row.disabled ? 'disabled' : '')}
          slots={{ toolbar }}
          variant="list"
          autoHeight={false}
          height={availableHeight}
        />
      </div>
      <ExpenseTypeActionModal
        expenseAction={expenseAction}
        onClose={() => setExpenseAction(null)}
        incomeAccounts={incomeAccounts}
        payableAccounts={payableAccounts}
        handleExpenseAction={handleExpenseAction}
        expenseRows={rows}
      />
      <DeleteExpenseTypeModal
        expenseTypeForDeletion={expenseTypeForDeletion}
        onClose={() => setExpenseTypeForDeletion(null)}
        onDelete={handleDeleteConfirm}
      />
    </>
  );
}
